import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import { getEmployees } from '../../services/api';

const QuickAssignmentDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  inventoryItem,
  formData,
  formErrors,
  onInputChange
}) => {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Load employees
  useEffect(() => {
    const loadEmployees = async () => {
      if (!open) return;
      
      setLoadingEmployees(true);
      try {
        const response = await getEmployees(1, 100); // Get first 100 employees
        setEmployees(response.data?.results || []);
      } catch (error) {
        console.error('Error loading employees:', error);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [open]);

  // Update selected employee when form data changes
  useEffect(() => {
    if (formData.assigned_to_id && employees.length > 0) {
      const employee = employees.find(emp => emp.id.toString() === formData.assigned_to_id.toString());
      setSelectedEmployee(employee);
    } else {
      setSelectedEmployee(null);
    }
  }, [formData.assigned_to_id, employees]);

  const handleSubmit = () => {
    // Basic validation
    if (!formData.assigned_to_id || !formData.quantity) {
      return;
    }
    
    const requestedQuantity = parseInt(formData.quantity, 10) || 0;
    if (requestedQuantity > (inventoryItem?.quantity || 0)) {
      return;
    }
    
    onSubmit();
  };

  const maxQuantity = inventoryItem?.quantity || 0;
  const requestedQuantity = parseInt(formData.quantity, 10) || 0;
  const hasQuantityError = requestedQuantity > maxQuantity;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Quick Assignment - {inventoryItem?.name}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Inventory Item Info */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon fontSize="small" />
                <strong>{inventoryItem?.name}</strong> - Available: {inventoryItem?.quantity}
              </Typography>
              {inventoryItem?.description && (
                <Typography variant="caption" color="text.secondary">
                  {inventoryItem.description}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Employee Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.assigned_to_id}>
              <InputLabel required>Employee</InputLabel>
              <Select
                value={formData.assigned_to_id || ''}
                label="Employee"
                onChange={(e) => onInputChange('assigned_to_id', e.target.value)}
                disabled={loadingEmployees}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.full_name || `${employee.first_name} ${employee.last_name}`} 
                    {employee.position && ` - ${employee.position}`}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.assigned_to_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.assigned_to_id}
                </Typography>
              )}
            </FormControl>
            {selectedEmployee && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Employee: {selectedEmployee.full_name || `${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
                  {selectedEmployee.email && ` (${selectedEmployee.email})`}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Quantity */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => onInputChange('quantity', e.target.value)}
              error={!!formErrors.quantity || hasQuantityError}
              helperText={
                formErrors.quantity || 
                (hasQuantityError ? `Cannot exceed available quantity (${maxQuantity})` : '')
              }
              required
              inputProps={{ min: 1, max: maxQuantity }}
            />
          </Grid>

          {/* Assignment Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Assignment Date"
              type="date"
              value={formData.assigned_date}
              onChange={(e) => onInputChange('assigned_date', e.target.value)}
              error={!!formErrors.assigned_date}
              helperText={formErrors.assigned_date}
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remarks"
              value={formData.remarks}
              onChange={(e) => onInputChange('remarks', e.target.value)}
              multiline
              rows={2}
              error={!!formErrors.remarks}
              helperText={formErrors.remarks}
              placeholder="Optional remarks about this assignment..."
            />
          </Grid>

          {hasQuantityError && (
            <Grid item xs={12}>
              <Alert severity="error">
                Requested quantity ({requestedQuantity}) exceeds available stock ({maxQuantity})
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={!formData.assigned_to_id || !formData.quantity || hasQuantityError || maxQuantity <= 0}
        >
          Assign Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

QuickAssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  inventoryItem: PropTypes.object,
  formData: PropTypes.object.isRequired,
  formErrors: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
};

QuickAssignmentDialog.defaultProps = {
  inventoryItem: null,
};

export default QuickAssignmentDialog;